import { type DrawerProps, Form, Space } from 'antd';
import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { type DisclaimerKind } from '.';
import { type DealerFinancingDisclaimer, type DealerInsuranceDisclaimer } from '../../../api/types';
import FormFields from '../../../themes/admin/Fields/FormFields';
import DrawerWithAutoWidth from '../../DrawerWithAutoWidth';
import { type SelectFieldProps } from '../SelectField';

export type DisclaimerMultipleEntryDrawerProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> &
    Pick<SelectFieldProps, 'options'> & {
        name: string;
        disclaimerKind: DisclaimerKind;
    };

const DisclaimerEntryDrawer = ({
    onClose,
    title,
    visible,
    name,
    disclaimerKind,
}: DisclaimerMultipleEntryDrawerProps) => {
    const [{ value }] = useField(name);
    const disclaimerList = value => {
        switch (disclaimerKind) {
            case 'financingDisclaimer':
                return value.map((financingDisclaimer: DealerFinancingDisclaimer, index: number) => (
                    <Form layout="vertical">
                        <FormFields.TranslatedInputField
                            label={financingDisclaimer.bankName}
                            name={`${name}[${index}].financingDisclaimer`}
                        />
                    </Form>
                ));

            case 'insuranceDisclaimer':
                return value.map((insuranceDisclaimer: DealerInsuranceDisclaimer, index: number) => (
                    <Form layout="vertical">
                        <FormFields.TranslatedInputField
                            label={insuranceDisclaimer.insurerName}
                            name={`${name}[${index}].insuranceDisclaimer`}
                        />
                    </Form>
                ));

            case 'priceDisclaimer':
                return (
                    <Form layout="vertical">
                        <FormFields.TranslatedInputField name={`${name}[0].priceDisclaimer`} />
                    </Form>
                );

            default:
                return null;
        }
    };

    const content = (
        <Space direction="vertical" style={{ width: '100%' }}>
            {value.length > 0 && disclaimerList(value)}
        </Space>
    );

    return (
        <DrawerWithAutoWidth
            maxWidth={40}
            onClose={onClose}
            placement="right"
            title={title}
            visible={visible}
            destroyOnClose
        >
            {content}
        </DrawerWithAutoWidth>
    );
};

export const useDisclaimerMultipleEntryDrawer = (disabled?: boolean) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => !disabled && setVisible(true),
            close: () => setVisible(false),
        }),
        [disabled]
    );

    return {
        ...actions,
        render: (props: Pick<DisclaimerMultipleEntryDrawerProps, 'name' | 'options' | 'title' | 'disclaimerKind'>) => (
            <DisclaimerEntryDrawer {...props} name={props.name} onClose={actions.close} visible={visible} />
        ),
    };
};
